import React, { useState } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { FormField, FormButton } from '../common/Form';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { TripFormData } from './TripWizardModal';

interface Step1Props {
  data: TripFormData;
  onChange: (data: Partial<TripFormData>) => void;
  onNext: () => void;
  isLoading: boolean;
}

interface Step1FormData {
  hotel_name: string;
  start_date: string;
  end_date: string;
}

const step1Schema = yup.object({
  hotel_name: yup
    .string()
    .required('Nome do hotel é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres'),
  start_date: yup
    .string()
    .required('Data de início é obrigatória')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
    .test('future-date', 'Data de início deve ser hoje ou no futuro', function(value) {
      if (!value) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(value);
      return startDate >= today;
    }),
  end_date: yup
    .string()
    .required('Data de fim é obrigatória')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
    .test('after-start', 'Data de fim deve ser após a data de início', function(value) {
      const { start_date } = this.parent;
      if (!value || !start_date) return false;
      return new Date(value) > new Date(start_date);
    }),
});

export function Step1Details({ data, onChange, onNext, isLoading }: Step1Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<Step1FormData>({
    resolver: yupResolver(step1Schema),
    defaultValues: {
      hotel_name: data.hotel_name,
      start_date: data.start_date,
      end_date: data.end_date,
    }
  });

  const watchedStartDate = watch('start_date');

  const onSubmit = async (formData: Step1FormData) => {
    setIsSubmitting(true);
    
    try {
      // Calculate total days
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      const timeDiff = endDate.getTime() - startDate.getTime();
      const total_days = Math.ceil(timeDiff / (1000 * 3600 * 24));

      // Update parent form data
      onChange({
        ...formData,
        total_days,
      });

      // Move to next step
      onNext();
    } catch (error) {
      console.error('Erro ao validar dados da viagem:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinEndDate = () => {
    if (watchedStartDate) {
      const startDate = new Date(watchedStartDate);
      startDate.setDate(startDate.getDate() + 1);
      return startDate.toISOString().split('T')[0];
    }
    return '';
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <MapPin className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">Detalhes da Viagem</h3>
        <p className="text-gray-600 mt-2">Informe o destino e as datas da sua viagem</p>
      </div>

      <div className="space-y-6">
        {/* Hotel Name */}
        <FormField
          label="Nome do Hotel/Pousada"
          name="hotel_name"
          type="text"
          placeholder="Ex: Hotel Paradise Beach, Pousada do Mar..."
          register={register}
          error={errors.hotel_name}
          required
          disabled={isLoading || isSubmitting}
          autoComplete="off"
        />

        {/* Date Range */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label="Data de Chegada"
            name="start_date"
            type="date"
            register={register}
            error={errors.start_date}
            required
            disabled={isLoading || isSubmitting}
            min={getTodayDate()}
          />

          <FormField
            label="Data de Saída"
            name="end_date"
            type="date"
            register={register}
            error={errors.end_date}
            required
            disabled={isLoading || isSubmitting}
            min={getMinEndDate()}
          />
        </div>

        {/* Trip Duration Display */}
        {watchedStartDate && watch('end_date') && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
              <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-blue-900">
                  Duração da viagem: {
                    Math.ceil((new Date(watch('end_date')).getTime() - new Date(watchedStartDate).getTime()) / (1000 * 3600 * 24))
                  } {
                    Math.ceil((new Date(watch('end_date')).getTime() - new Date(watchedStartDate).getTime()) / (1000 * 3600 * 24)) === 1 ? 'dia' : 'dias'
                  }
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Taxa de preservação ambiental será calculada com base na duração
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Information Box */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">ℹ️ Informações importantes:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• A taxa de preservação ambiental é de R$ 4,15 por dia, por pessoa</li>
            <li>• Valor mínimo de R$ 41,50 por pessoa (mesmo para estadias menores)</li>
            <li>• Menores de 12 anos e maiores de 60 anos são isentos</li>
            <li>• Outros tipos de isenção podem ser solicitados na próxima etapa</li>
          </ul>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-6">
        <FormButton
          type="submit"
          variant="primary"
          loading={isSubmitting}
          disabled={isLoading || isSubmitting}
          className="min-w-[120px]"
        >
          Próximo
        </FormButton>
      </div>
    </form>
  );
}

export default Step1Details;
